import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import ModalBlank from "../../../ui/modals/ModalBlank";
import CategoryForm from "../components/CategoryForm";
import CategoryList from "../components/CategoryList";
import DayEvents from "../components/DayEvents";
import EventDetails from "../components/EventDetails";
import EventForm from "../components/EventForm";
import { useCalendar } from "../hooks/useCalendar";
import {
  CalendarEvent,
  CreateCategoryData,
  CreateEventData,
  EventCategory,
  UpdateCategoryData,
  UpdateEventData,
} from "../types/calendar.types";

const CalendarPage: React.FC = () => {
  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  const {
    events,
    categories,
    formattedEvents,
    activeCategory,
    loading,
    error,
    fetchCalendarData,
    createEvent,
    updateEvent,
    deleteEvent,
    createCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryFilter,
    getEventsForDate,
  } = useCalendar();

  const today = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [daysInMonth, setDaysInMonth] = useState<number[]>([]);
  const [startingBlankDays, setStartingBlankDays] = useState<number[]>([]);
  const [endingBlankDays, setEndingBlankDays] = useState<number[]>([]);

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] =
    useState<EventCategory | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate days in month
  const getDays = () => {
    const days = new Date(year, month + 1, 0).getDate();

    // Starting empty cells (previous month)
    const startingDayOfWeek = new Date(year, month, 1).getDay();
    let startingBlankDaysArray = [];
    for (let i = 1; i <= startingDayOfWeek; i++) {
      startingBlankDaysArray.push(i);
    }

    // Ending empty cells (next month)
    const endingDayOfWeek = new Date(year, month + 1, 0).getDay();
    let endingBlankDaysArray = [];
    for (let i = 1; i < 7 - endingDayOfWeek; i++) {
      endingBlankDaysArray.push(i);
    }

    // Current month cells
    let daysArray = [];
    for (let i = 1; i <= days; i++) {
      daysArray.push(i);
    }

    setStartingBlankDays(startingBlankDaysArray);
    setEndingBlankDays(endingBlankDaysArray);
    setDaysInMonth(daysArray);
  };

  // Check if a date is today
  const isToday = (date: number): boolean => {
    const day = new Date(year, month, date);
    return today.toDateString() === day.toDateString();
  };

  // Get events for a specific date
  const getEvents = (date: number): CalendarEvent[] => {
    const day = new Date(year, month, date);
    return events.filter((e) => {
      const eventDate = e.startDate.toDate();
      return (
        eventDate.getDate() === day.getDate() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getFullYear() === day.getFullYear() &&
        (!activeCategory || e.categoryId === activeCategory)
      );
    });
  };

  // Get color for an event based on its category
  const eventColor = (categoryId: string): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.color || "gray";
  };

  // Handle event creation
  const handleCreateEvent = async (data: CreateEventData) => {
    try {
      setIsSubmitting(true);
      await createEvent(data);
    } catch (error) {
      console.error("Error creating event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle event update
  const handleUpdateEvent = async (data: UpdateEventData) => {
    if (!selectedEvent) return;

    try {
      setIsSubmitting(true);
      await updateEvent(selectedEvent.id, data);
    } catch (error) {
      console.error("Error updating event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      setIsSubmitting(true);
      await deleteEvent(selectedEvent.id);
      setShowEventModal(false);
      setShowEventDetailsModal(false);
    } catch (error) {
      console.error("Error deleting event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category creation
  const handleCreateCategory = async (data: CreateCategoryData) => {
    try {
      setIsSubmitting(true);
      await createCategory(data);
    } catch (error) {
      console.error("Error creating category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category update
  const handleUpdateCategory = async (data: UpdateCategoryData) => {
    if (!selectedCategory) return;

    try {
      setIsSubmitting(true);
      await updateCategory(selectedCategory.id, data);
    } catch (error) {
      console.error("Error updating category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category deletion
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      setIsSubmitting(true);
      await deleteCategory(selectedCategory.id);
      setShowCategoryModal(false);
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open event form modal for creating a new event
  const openCreateEventModal = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  // Open event form modal for editing an existing event
  const openEditEventModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    setShowEventDetailsModal(false);
  };

  // Open event details modal
  const openEventDetailsModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventDetailsModal(true);
    setShowDayEventsModal(false);
  };

  // Open category form modal for creating a new category
  const openCreateCategoryModal = () => {
    setSelectedCategory(null);
    setShowCategoryModal(true);
  };

  // Open category form modal for editing an existing category
  const openEditCategoryModal = (category: EventCategory) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  // Open day events modal
  const openDayEventsModal = (date: number) => {
    setSelectedDate(new Date(year, month, date));
    setShowDayEventsModal(true);
  };

  // Handle category click for filtering
  const handleCategoryClick = (categoryId: string) => {
    toggleCategoryFilter(categoryId);
  };

  // Initialize days on mount and when month/year changes
  useEffect(() => {
    getDays();
  }, [month, year]);

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: "Key Dates",
        actions: isAdmin
          ? [
              {
                label: "Create Event",
                onClick: openCreateEventModal,
              },
            ]
          : undefined,
      }}
      isLoading={loading}
      error={error ? new Error(error) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Calendar header */}
        <div className="sm:flex sm:justify-between sm:items-center mb-4">
          {/* Month/Year display */}
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              <span>{`${monthNames[month]} ${year}`}</span>
            </h1>
          </div>

          {/* Navigation buttons */}
          <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
            {/* Previous month button */}
            <button
              className="btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
              disabled={month === 0 && year === today.getFullYear() - 10}
              onClick={() => {
                if (month === 0) {
                  setMonth(11);
                  setYear(year - 1);
                } else {
                  setMonth(month - 1);
                }
              }}
            >
              <span className="sr-only">Previous month</span>
              <svg
                className="fill-current text-gray-400 dark:text-gray-500"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M9.4 13.4l1.4-1.4-4-4 4-4-1.4-1.4L4 8z" />
              </svg>
            </button>

            {/* Next month button */}
            <button
              className="btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
              disabled={month === 11 && year === today.getFullYear() + 10}
              onClick={() => {
                if (month === 11) {
                  setMonth(0);
                  setYear(year + 1);
                } else {
                  setMonth(month + 1);
                }
              }}
            >
              <span className="sr-only">Next month</span>
              <svg
                className="fill-current text-gray-400 dark:text-gray-500"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M6.6 13.4L5.2 12l4-4-4-4 1.4-1.4L12 8z" />
              </svg>
            </button>

            <hr className="w-px h-full bg-gray-200 dark:bg-gray-700/60 border-none mx-1" />

            {/* Today button */}
            <button
              className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              onClick={() => {
                setMonth(today.getMonth());
                setYear(today.getFullYear());
              }}
            >
              Today
            </button>
          </div>
        </div>

        {/* Categories and view buttons */}
        <div className="sm:flex sm:justify-between sm:items-center mb-4">
          {/* Categories */}
          <div className="mb-4 sm:mb-0 mr-2">
            <CategoryList
              categories={categories}
              activeCategory={activeCategory}
              onCategoryClick={handleCategoryClick}
              onEditCategory={isAdmin ? openEditCategoryModal : undefined}
              isAdmin={isAdmin}
              onAddCategory={isAdmin ? openCreateCategoryModal : undefined}
            />
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {/* Days of the week */}
          <div className="grid grid-cols-7 gap-px border-b border-gray-200 dark:border-gray-700/60">
            {dayNames.map((day) => (
              <div className="px-1 py-3" key={day}>
                <div className="text-gray-500 text-sm font-medium text-center lg:hidden">
                  {day.substring(0, 3)}
                </div>
                <div className="text-gray-500 dark:text-gray-400 text-sm font-medium text-center hidden lg:block">
                  {day}
                </div>
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700/60">
            {/* Diagonal stripes pattern */}
            <svg className="sr-only">
              <defs>
                <pattern
                  id="stripes"
                  patternUnits="userSpaceOnUse"
                  width="5"
                  height="5"
                  patternTransform="rotate(135)"
                >
                  <line
                    className="stroke-current text-gray-200 dark:text-gray-700 opacity-50"
                    x1="0"
                    y="0"
                    x2="0"
                    y2="5"
                    strokeWidth="2"
                  />
                </pattern>
              </defs>
            </svg>

            {/* Empty cells (previous month) */}
            {startingBlankDays.map((blankday) => (
              <div
                className="bg-gray-50 dark:bg-gray-800 h-20 sm:h-28 lg:h-36"
                key={`prev-${blankday}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                >
                  <rect width="100%" height="100%" fill="url(#stripes)" />
                </svg>
              </div>
            ))}

            {/* Days of the current month */}
            {daysInMonth.map((day) => {
              const dayEvents = getEvents(day);

              return (
                <div
                  className="relative bg-white dark:bg-gray-800 h-20 sm:h-28 lg:h-36 overflow-hidden cursor-pointer"
                  key={`day-${day}`}
                  onClick={() => openDayEventsModal(day)}
                >
                  <div className="h-full flex flex-col justify-between">
                    {/* Events */}
                    <div className="grow flex flex-col relative p-0.5 sm:p-1.5 overflow-hidden">
                      {dayEvents.slice(0, 2).map((event) => {
                        const color = eventColor(event.categoryId);

                        return (
                          <motion.button
                            key={event.id}
                            className="relative w-full text-left mb-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEventDetailsModal(event);
                            }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div
                              className={`px-2 py-0.5 rounded-lg overflow-hidden text-white bg-${color}-500`}
                            >
                              {/* Event name */}
                              <div className="text-xs font-semibold truncate">
                                {event.title}
                              </div>
                              {/* Event time */}
                              <div className="text-xs uppercase truncate hidden sm:block">
                                {/* Start date */}
                                <span>
                                  {event.startDate
                                    .toDate()
                                    .toLocaleTimeString([], {
                                      hour12: true,
                                      hour: "numeric",
                                      minute: "numeric",
                                    })}
                                </span>
                                {/* End date */}
                                {event.endDate && (
                                  <span>
                                    -{" "}
                                    <span>
                                      {event.endDate
                                        .toDate()
                                        .toLocaleTimeString([], {
                                          hour12: true,
                                          hour: "numeric",
                                          minute: "numeric",
                                        })}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                      <div
                        className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"
                        aria-hidden="true"
                      ></div>
                    </div>

                    {/* Cell footer */}
                    <div className="flex justify-between items-center p-0.5 sm:p-1.5">
                      {/* More button (if more than 2 events) */}
                      {dayEvents.length > 2 && (
                        <button
                          className="text-xs text-gray-500 dark:text-gray-300 font-medium whitespace-nowrap text-center sm:py-0.5 px-0.5 sm:px-2 border border-gray-200 dark:border-gray-700/60 rounded-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDayEventsModal(day);
                          }}
                        >
                          <span className="md:hidden">+</span>
                          <span>{dayEvents.length - 2}</span>{" "}
                          <span className="hidden md:inline">more</span>
                        </button>
                      )}

                      {/* Day number */}
                      <button
                        className={`inline-flex ml-auto w-6 h-6 items-center justify-center text-xs sm:text-sm dark:text-gray-300 font-medium text-center rounded-full hover:bg-violet-100 dark:hover:bg-gray-600 ${
                          isToday(day) ? "text-violet-500" : ""
                        }`}
                      >
                        {day}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Empty cells (next month) */}
            {endingBlankDays.map((blankday) => (
              <div
                className="bg-gray-50 dark:bg-gray-800 h-20 sm:h-28 lg:h-36"
                key={`next-${blankday}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                >
                  <rect width="100%" height="100%" fill="url(#stripes)" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Form Modal */}
      <ModalBlank
        id="event-form-modal"
        className="p-6"
        modalOpen={showEventModal}
        setModalOpen={setShowEventModal}
        showCloseButton={true}
      >
        {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full p-6"></div> */}
        <EventForm
          event={selectedEvent || undefined}
          categories={categories}
          onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent}
          onDelete={selectedEvent ? handleDeleteEvent : undefined}
          onCancel={() => setShowEventModal(false)}
          isSubmitting={isSubmitting}
        />
      </ModalBlank>

      {/* Category Form Modal */}
      <ModalBlank
        id="category-form-modal"
        className="p-6"
        modalOpen={showCategoryModal}
        setModalOpen={setShowCategoryModal}
        showCloseButton={true}
      >
        <CategoryForm
          category={selectedCategory || undefined}
          onSubmit={
            selectedCategory ? handleUpdateCategory : handleCreateCategory
          }
          onDelete={selectedCategory ? handleDeleteCategory : undefined}
          onCancel={() => setShowCategoryModal(false)}
          isSubmitting={isSubmitting}
        />
      </ModalBlank>

      {/* Event Details Modal */}
      <ModalBlank
        id="event-details-modal"
        className="p-6"
        modalOpen={showEventDetailsModal}
        setModalOpen={setShowEventDetailsModal}
        showCloseButton={true}
      >
        {selectedEvent && (
          <EventDetails
            event={selectedEvent}
            category={categories.find(
              (cat) => cat.id === selectedEvent.categoryId
            )}
            onEdit={
              isAdmin ? () => openEditEventModal(selectedEvent) : undefined
            }
            onClose={() => setShowEventDetailsModal(false)}
            isAdmin={isAdmin}
          />
        )}
      </ModalBlank>

      {/* Day Events Modal */}
      <ModalBlank
        id="day-events-modal"
        className="p-6"
        modalOpen={showDayEventsModal}
        setModalOpen={setShowDayEventsModal}
        showCloseButton={true}
      >
        {selectedDate && (
          <DayEvents
            date={selectedDate}
            events={getEventsForDate(selectedDate)}
            categories={categories}
            onEventClick={openEventDetailsModal}
          />
        )}
      </ModalBlank>
    </AuthenticatedLayout>
  );
};

export default CalendarPage;
